/**
 * Deterministic pre-publish checks. Pure functions — no network, no LLM.
 * Fail-level checks block publishing; warn-level checks are advisory.
 */

import { TEMPLATES } from "./templates";
import { lintTerminology } from "./terminology";
import { noticeWindow } from "./material-change";
import { canPublish, isDocStatus } from "../../shared/types";

export type CheckLevel = "pass" | "warn" | "fail";

export interface PublishCheck {
  level: CheckLevel;
  label: string;
}

/** Minimal doc-entry shape needed for link resolution (subset of GitDocEntry). */
export interface LinkTarget {
  path: string;
  family: string;
}

export interface PublishCheckInput {
  status: string;
  title: string;
  version: string;
  family: string;
  docType: string;
  effectiveDate: string;
  /** Markdown body, front matter already stripped. */
  body: string;
  /** Git tree entries used to resolve internal links. */
  docs: LinkTarget[];
  /** Whether this publish is flagged as a material methodology change. */
  materialChange?: boolean;
  /** ISO date the change was announced (defaults to today in the check). */
  noticeDate?: string;
}

function truncate(s: string, max = 60): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** 1. Status must be `review` (blocking). */
function checkStatus(input: PublishCheckInput): PublishCheck[] {
  if (isDocStatus(input.status) && canPublish(input.status)) {
    return [{ level: "pass", label: "status is review" }];
  }
  return [
    {
      level: "fail",
      label: `status is ${input.status || "unknown"} — set to review in the editor`,
    },
  ];
}

/** 2. Required front-matter metadata must be non-empty (blocking). */
function checkRequiredMetadata(input: PublishCheckInput): PublishCheck[] {
  const required: Array<[string, string]> = [
    ["title", input.title],
    ["version", input.version],
    ["family", input.family],
    ["doc_type", input.docType],
    ["effective_date", input.effectiveDate],
  ];
  const missing = required.filter(([, value]) => !value || !value.trim());
  if (missing.length === 0) {
    return [{ level: "pass", label: "required metadata present" }];
  }
  return missing.map(([field]) => ({
    level: "fail" as const,
    label: `missing metadata: ${field}`,
  }));
}

/** 3. Required template sections must appear as headings (warn). */
function checkRequiredSections(input: PublishCheckInput): PublishCheck[] {
  const template = TEMPLATES[input.docType];
  if (!template) {
    return [{ level: "pass", label: "no template sections defined" }];
  }
  // TOC sections are auto-generated, never present in the markdown body
  // (mirrors generateTemplateMarkdown).
  const required = template.sections.filter(
    (s) => s.required && !(s.content === "metadata" && s.id === "toc"),
  );
  if (required.length === 0) {
    return [{ level: "pass", label: "no template sections defined" }];
  }

  const headings = input.body
    .split("\n")
    .filter((line) => /^#{1,6}\s/.test(line))
    .map((line) => line.replace(/^#{1,6}\s+/, "").toLowerCase());

  const missing = required.filter(
    (s) => !headings.some((h) => h.includes(s.label.toLowerCase())),
  );
  if (missing.length === 0) {
    return [{ level: "pass", label: "required sections present" }];
  }
  return missing.map((s) => ({
    level: "warn" as const,
    label: `missing section: ${s.label}`,
  }));
}

/** Markdown link targets: [text](target "optional title") */
const LINK_RE = /\[[^\]]*\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/g;

/** 4. Internal links resolve against the git tree; external URLs parse (warn). */
function checkLinks(input: PublishCheckInput): PublishCheck[] {
  const known = new Set<string>();
  for (const d of input.docs) {
    known.add(d.path);
    known.add(d.path.replace(/\.md$/, ""));
    known.add(d.family);
    const slug = d.family.split("/").pop();
    if (slug) known.add(slug);
  }

  const issues: PublishCheck[] = [];
  for (const match of input.body.matchAll(LINK_RE)) {
    const target = match[1];
    if (!target || target.startsWith("#")) continue;
    if (/^(mailto|tel):/i.test(target)) continue;
    if (/^https?:\/\//i.test(target)) {
      try {
        new URL(target);
      } catch {
        issues.push({
          level: "warn",
          label: `malformed url: ${truncate(target)}`,
        });
      }
      continue;
    }
    // Internal link — normalize and resolve against the git tree.
    const norm = target
      .replace(/[?#].*$/, "")
      .replace(/^\.\//, "")
      .replace(/^\//, "");
    if (!norm || known.has(norm) || known.has(norm.replace(/\.md$/, "")))
      continue;
    issues.push({
      level: "warn",
      label: `unresolved link: ${truncate(target)}`,
    });
  }
  if (issues.length === 0) {
    return [{ level: "pass", label: "internal links resolve" }];
  }
  return issues;
}

/** 5. No TODO/FIXME/TKTK markers in the body (warn). */
function checkTodoMarkers(input: PublishCheckInput): PublishCheck[] {
  const matches = input.body.match(/\b(?:TODO|FIXME|TKTK)\b/g);
  if (!matches) {
    return [{ level: "pass", label: "no todo markers" }];
  }
  const n = matches.length;
  return [
    {
      level: "warn",
      label: `${n} todo/fixme marker${n === 1 ? "" : "s"} in body`,
    },
  ];
}

/** Max terminology findings surfaced as individual warn lines. */
const TERMINOLOGY_LINE_CAP = 8;

/** 7. House-style / terminology lint over the body (warn only, never blocking). */
function checkTerminology(input: PublishCheckInput): PublishCheck[] {
  const findings = lintTerminology(input.body);
  if (findings.length === 0) {
    return [{ level: "pass", label: "terminology consistent" }];
  }
  const shown = findings.slice(0, TERMINOLOGY_LINE_CAP);
  const lines: PublishCheck[] = shown.map((f) => ({
    level: "warn" as const,
    label: `line ${f.line}: ${f.message} (${truncate(f.match, 24)})`,
  }));
  const overflow = findings.length - shown.length;
  if (overflow > 0) {
    lines.push({
      level: "warn",
      label: `terminology: ${overflow} more style note${overflow === 1 ? "" : "s"}`,
    });
  }
  return lines;
}

/**
 * 8. Material changes must give the minimum advance-notice window (warn, never
 *    blocking). Non-material changes emit nothing.
 */
function checkMaterialChange(input: PublishCheckInput): PublishCheck[] {
  if (!input.materialChange) return [];
  const window = noticeWindow({
    material: true,
    noticeDate: input.noticeDate,
    effectiveDate: input.effectiveDate,
  });
  if (window.ok) {
    return [
      {
        level: "pass",
        label: `material change: ${window.daysNotice} days notice (ok)`,
      },
    ];
  }
  const shortfall = input.effectiveDate
    ? `only ${window.daysNotice} days notice (needs ${window.requiredDays})`
    : `effective date required (needs ${window.requiredDays} days notice)`;
  return [{ level: "warn", label: `material change: ${shortfall}` }];
}

export function runPublishChecks(input: PublishCheckInput): PublishCheck[] {
  return [
    ...checkStatus(input),
    ...checkRequiredMetadata(input),
    ...checkRequiredSections(input),
    ...checkLinks(input),
    ...checkTodoMarkers(input),
    ...checkTerminology(input),
    ...checkMaterialChange(input),
  ];
}

export function hasBlockingFailures(checks: PublishCheck[]): boolean {
  return checks.some((c) => c.level === "fail");
}
